import { useCallback, useEffect, useRef, useState } from 'react';
import { useRoomContext, useRoomPhase } from '@interactive-room';

interface UseExamIntegrityOptions {
  displayName: string;
  enabled: boolean;
  locked?: boolean;
}

export function useExamIntegrity({ displayName, enabled, locked }: UseExamIntegrityOptions) {
  const { reportExamBreach } = useRoomContext();
  const { phase } = useRoomPhase();
  const [fullscreenOk, setFullscreenOk] = useState(false);
  const [blurFlash, setBlurFlash] = useState(false);
  const reportedRef = useRef(false);

  const active = enabled && phase === 'ACTIVE' && !locked;

  const triggerBreach = useCallback(
    (reason: 'visibility' | 'blur' | 'fullscreen_exit') => {
      if (!active || reportedRef.current) return;
      reportedRef.current = true;
      void reportExamBreach(reason);
    },
    [active, reportExamBreach],
  );

  const enterFullscreen = useCallback(async () => {
    try {
      await document.documentElement.requestFullscreen();
      setFullscreenOk(true);
    } catch {
      setFullscreenOk(false);
    }
  }, []);

  useEffect(() => {
    if (!active) return;

    const onVisibility = () => {
      if (document.hidden) triggerBreach('visibility');
    };

    const onBlur = () => {
      triggerBreach('blur');
    };

    const onFullscreen = () => {
      if (!document.fullscreenElement) {
        triggerBreach('fullscreen_exit');
      }
    };

    const onPrintScreen = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        setBlurFlash(true);
        window.setTimeout(() => setBlurFlash(false), 500);
      }
    };

    window.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    document.addEventListener('fullscreenchange', onFullscreen);
    window.addEventListener('keyup', onPrintScreen);

    return () => {
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('fullscreenchange', onFullscreen);
      window.removeEventListener('keyup', onPrintScreen);
    };
  }, [active, triggerBreach]);

  useEffect(() => {
    if (!enabled) reportedRef.current = false;
  }, [enabled]);

  return {
    active,
    fullscreenOk,
    blurFlash,
    enterFullscreen,
    watermarkLabel: displayName,
  };
}
